import { useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Link2, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const BRIDGE_VIDEO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/h6sc3ray_gemini_generated_video_da30dbd5.mp4";

export function IdeaBridge() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const samples = t("atelier.bridge.samples", { returnObjects: true }) || [];

  const build = async (aa, bb) => {
    const x = (aa ?? a).trim();
    const y = (bb ?? b).trim();
    if (!x || !y || loading) return;
    setA(x); setB(y);
    setLoading(true); setData(null);
    try {
      const { data: res } = await api.get("/bridge", { params: { a: x, b: y, lang: (i18n.language || "fr").slice(0, 2) } });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || t("atelier.bridge.error"));
    } finally { setLoading(false); }
  };

  const deepen = () => {
    if (!data) return;
    localStorage.setItem("coccon_pending_prompt", t("atelier.bridge.deepenPrompt", { a: data.a, b: data.b }));
    navigate("/atelier");
  };

  return (
    <div data-testid="idea-bridge">
      <div className="text-center mb-6">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Link2 className="text-primary pulse-star" size={22} strokeWidth={1.7} />
        </div>
        <p className="uppercase tracking-[0.25em] text-xs text-primary/80 mb-2">{t("atelier.bridge.title")}</p>
        <p className="text-foreground/55 max-w-lg mx-auto leading-relaxed text-sm">{t("atelier.bridge.subtitle")}</p>
      </div>

      <div className="max-w-3xl mx-auto mb-8">
        <CinematicVideo fade noSound src={BRIDGE_VIDEO} testId="bridge-cinematic-video" />
      </div>

      <form onSubmit={(e) => { e.preventDefault(); build(); }} className="flex flex-col sm:flex-row items-stretch gap-2 mb-4 max-w-2xl mx-auto">
        <input value={a} onChange={(e) => setA(e.target.value)} placeholder={t("atelier.bridge.placeholderA")}
          data-testid="bridge-input-a"
          className="flex-1 rounded-full bg-white/[0.03] border border-white/12 px-5 py-3 text-sm text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors" />
        <span className="hidden sm:flex items-center text-primary/60 font-display text-lg px-1">+</span>
        <input value={b} onChange={(e) => setB(e.target.value)} placeholder={t("atelier.bridge.placeholderB")}
          data-testid="bridge-input-b"
          className="flex-1 rounded-full bg-white/[0.03] border border-white/12 px-5 py-3 text-sm text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors" />
        <button type="submit" disabled={loading || !a.trim() || !b.trim()} data-testid="bridge-go"
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50 flex-shrink-0">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {t("atelier.bridge.go")}
        </button>
      </form>

      {!data && !loading && (
        <div className="flex flex-wrap justify-center gap-2 mb-2" data-testid="bridge-samples">
          <span className="text-xs text-foreground/40 self-center mr-1">{t("atelier.bridge.sampleLabel")}</span>
          {samples.map((pair, i) => (
            <button key={i} onClick={() => build(pair[0], pair[1])} disabled={loading}
              data-testid={`bridge-sample-${i}`}
              className="text-xs px-3 py-1.5 rounded-full border border-primary/20 text-foreground/65 hover:text-primary hover:border-primary/40 transition-colors">
              {pair[0]} + {pair[1]}
            </button>
          ))}
        </div>
      )}

      {loading && !data && (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-6" data-testid="bridge-loading">
          <Loader2 size={16} className="animate-spin" /> {t("atelier.bridge.loading")}
        </p>
      )}

      {data && (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="glass stats-wow rounded-2xl border border-primary/15 p-6 mt-3" data-testid="bridge-result">
          <p className="text-center font-display text-2xl text-gradient-orange mb-1">{data.a} <span className="text-primary/50 text-lg">✕</span> {data.b}</p>
          {data.title && <p className="text-center font-display italic text-foreground/70 mb-4">{data.title}</p>}
          <p className="text-foreground/85 leading-relaxed">{data.bridge}</p>
          {data.spark && (
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="uppercase tracking-[0.2em] text-[0.7rem] text-primary/70 mb-1.5 flex items-center gap-1.5"><Sparkles size={12} /> {t("atelier.bridge.spark")}</p>
              <p className="font-display italic text-foreground/80 leading-relaxed">{data.spark}</p>
            </div>
          )}
          <div className="text-center mt-5">
            <button onClick={deepen} data-testid="bridge-deepen"
              className="btn-frost-orange inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold text-sm">
              {t("atelier.bridge.deepen")} <ArrowRight size={15} />
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
